import { useEffect, useState } from 'react';
import {
  Modal, Stack, TextInput, Button, Group, Text, SimpleGrid, UnstyledButton, Checkbox, Box,
} from '@mantine/core';
import { Play } from 'lucide-react';
import { modeMeta } from './modeMeta';
import type { GameType, Group as GroupType } from '../api';

interface Props {
  opened: boolean;
  gameTypes: GameType[];
  groups: GroupType[];
  saving?: boolean;
  onClose: () => void;
  onSubmit: (data: { name: string; gameTypeId: string; groupIds: number[] }) => void;
}

export function NewGameModal({ opened, gameTypes, groups, saving, onClose, onSubmit }: Props) {
  const enabled = gameTypes.filter((t) => t.enabled ?? t.default_enabled);
  const [name, setName] = useState('');
  const [typeId, setTypeId] = useState<string | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [nameError, setNameError] = useState<string | null>(null);

  useEffect(() => {
    if (!opened) return;
    setName('');
    setTypeId(enabled[0]?.id ?? null);
    setSelected(groups.map((g) => g.id));
    setNameError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) { setNameError('Doplň název hry'); return; }
    if (!typeId) return;
    onSubmit({ name: name.trim(), gameTypeId: typeId, groupIds: selected });
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Nová hra" size="lg">
      <form onSubmit={submit}>
        <Stack gap="sm">
          <TextInput
            label="Název hry"
            placeholder="např. Noční obvolávání"
            value={name}
            error={nameError}
            onChange={(e) => { setName(e.currentTarget.value); setNameError(null); }}
            data-autofocus
          />

          <Box>
            <Text fz="sm" fw={500} mb={4}>Herní mód</Text>
            <SimpleGrid cols={{ base: 1, xs: 2 }} spacing={8}>
              {enabled.map((t) => {
                const meta = modeMeta(t.id);
                const Icon = meta.icon;
                const active = typeId === t.id;
                return (
                  <UnstyledButton
                    key={t.id}
                    onClick={() => setTypeId(t.id)}
                    p={9}
                    style={(th) => ({
                      display: 'flex', alignItems: 'center', gap: 8,
                      border: `1px solid ${active ? 'var(--mantine-primary-color-filled)' : 'var(--mantine-color-default-border)'}`,
                      background: active ? 'var(--mantine-primary-color-light)' : 'transparent',
                      borderRadius: th.radius.sm, fontSize: 13, fontWeight: 600,
                    })}
                  >
                    <Icon size={15} color={`var(--mantine-color-${meta.color}-6)`} />
                    {t.name || meta.label}
                  </UnstyledButton>
                );
              })}
            </SimpleGrid>
            {enabled.length === 0 && (
              <Text c="orange" fz="sm">Není zapnutý žádný herní mód. Zapni ho v Nastavení → Herní módy.</Text>
            )}
          </Box>

          <Box>
            <Text fz="sm" fw={500} mb={4}>Skupiny ve hře</Text>
            <Group gap={6}>
              {groups.map((g) => (
                <Checkbox
                  key={g.id}
                  size="xs"
                  color={g.color || undefined}
                  label={g.name}
                  checked={selected.includes(g.id)}
                  onChange={(e) =>
                    setSelected((prev) =>
                      e.currentTarget.checked ? [...prev, g.id] : prev.filter((id) => id !== g.id)
                    )
                  }
                />
              ))}
            </Group>
            {groups.length === 0 && <Text c="dimmed" fz="sm">Zatím nemáš žádné skupiny.</Text>}
          </Box>

          <Group justify="flex-end" mt="xs">
            <Button variant="default" type="button" onClick={onClose}>Zrušit</Button>
            <Button type="submit" leftSection={<Play size={14} />} loading={saving} disabled={enabled.length === 0}>
              Založit a spustit
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
